/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, useContext, useRef } from 'react';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { useParams } from 'react-router';
import apiAdmin from '../../services/api-admin';
import { MdEdit, MdAdd, MdOutlineEmail, MdOutlineFileDownload, MdCheckCircle, MdSearch, MdVisibility } from 'react-icons/md';
import { useForm, Controller } from "react-hook-form";
import { AdminUserContext } from '../../contexts/user-context';
import CustomDataGridToolbar from '../layout/custom-toolbar';
import { ToggleButtonGroup, ToggleButton, MenuItem, Select, FormControl, InputLabel, OutlinedInput, TextField } from '@mui/material';
import AssessmentQuestion from './builder-assessment-questions';

const BuilderAssessment = (props) => {
    const { adminUserProfile } = useContext(AdminUserContext);
    const { register, handleSubmit, watch, reset, setValue, formState: { errors }, control } = useForm();
    const [assessments, setAssessments] = useState([]);
    const [assessmentIdToEdit, setAssessmentIdToEdit] = useState(null);
    const [assessmentToEdit, setAssessmentToEdit] = useState();
    const [assessmentNotes, setAssessmentNotes] = useState([]);
    const [selectedAssessmentData, setSelectedAssessmentData] = useState(null);
    const [selectedBuilderName, setSelectedBuilderName] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [buildersToShow, setBuildersToShow] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [leadershipTeam, setLeadershipTeam] = useState();
    const [builderStatus, setBuilderStatus] = useState('Needs Approval');
    const [toggleButtonLabels, setToggleButtonLabels] = useState({ needsApproval: "Needs Approval", approved: "Approved", denied: "Denied", });
    const [showNeedsApprovalModal, setShowNeedsApprovalModal] = useState(false);
    const [showApprovedModal, setShowApprovedModal] = useState(false);
    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [assessmentIdToApprove, setAssessmentIdToApprove] = useState(null);
    const [sending, setSending] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [decisionSaved, setDecisionSaved] = useState(false);
    const [sendingDecision, setSendingDecision] = useState(false);
    const [builderQuery, setBuilderQuery] = useState('');
    const [orderSummary, setOrderSummary] = useState(null);
    const [loadingBuilderSearch, setLoadingBuilderSearch] = useState(false);
    const [orderSummaryDisplay, setOrderSummaryDisplay] = useState([]);
    const [existingAssessmentId, setExistingAssessmentId] = useState(null);
    const [builderSearchCompleted, setBuilderSearchCompleted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    let { paramAssessment } = useParams();
    const noteRef = useRef();
    const statusRef = useRef();
    const isManagementUser = adminUserProfile.memberOf?.includes("ManagementUsers");

    const columns = [
        { field: 'id', headerName: 'Id', export: true, flex: 50 },
        { field: 'builder', headerName: 'Builder', flex: 350 },
        {
            field: 'submissionDate', headerName: 'Submission Date', flex: 150,
            renderCell: function (t) {
                return t.value.toLocaleDateString("en-US");
            }
        },
        { field: 'submittedByName', headerName: 'Submitted By', flex: 250 },
        {
            field: 'assessedDate', headerName: 'Assessed Date', flex: 150,
            renderCell: function (t) {
                return t.value ? new Date(t.value).toLocaleDateString("en-US") : "";
            }
        },
        { field: 'assessedByName', headerName: 'Assessed By', flex: 250 },
        {
            field: 'actions', type: 'actions', headerName: 'Actions', flex: 100, cellClassName: 'actions',
            getActions: ({ id, row }) => {
                const actions = [];

                if (builderStatus === "Needs Approval" && isManagementUser) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<MdVisibility />}
                            label="View"
                            onClick={() => {
                                setAssessmentIdToApprove(id);
                                setAssessmentIdToEdit(id);
                            }}
                            color="inherit"
                        />
                    );

                    actions.push(
                        <GridActionsCellItem
                            icon={<MdCheckCircle />}
                            label="Approve"
                            onClick={() => {
                                setAssessmentIdToApprove(id);
                                setShowDecisionModal(true);
                            }}
                            color="inherit"
                        />
                    );
                } else {
                    actions.push(
                        <GridActionsCellItem
                            icon={<MdEdit />}
                            label="Edit"
                            onClick={() => {
                                setAssessmentIdToEdit(id);
                                setAssessmentIdToApprove(null);
                            }}
                            color="inherit"
                        />
                    );
                }

                if (builderStatus === "Approved") {
                    actions.push(
                        <GridActionsCellItem
                            icon={<MdOutlineFileDownload />}
                            label="Download PDF"
                            onClick={() => downloadAssessmentPdf(id)}
                            color="inherit"
                        />
                    );
                }

                return actions;
            }
        }
    ];

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 300,
                width: 250,
            },
        },
    };

    useEffect(() => {
        const getAssessments = async () => {
            apiAdmin.get(`/get-assessments`)
                .then((response) => {
                    setToggleButtonLabels({
                        needsApproval: "Needs Approval (" + response.data.filter(r => r.assessmentState === "Needs Approval").length + ")",
                        approved: "Approved (" + response.data.filter(r => r.assessmentState && r.assessmentState.includes("Approved")).length + ")",
                        denied: "Denied (" + response.data.filter(r => r.assessmentState && r.assessmentState.includes("Denied")).length + ")"
                    });

                    setAssessments(response.data.map(a => ({
                        id: a.builderAssessmentId,
                        builder: a.builderName,
                        submittedByName: a.submittedBy,
                        submissionDate: new Date(a.submissionDate),
                        assessedByName: a.assessedBy,
                        assessedDate: a.assessedDate ? new Date(a.assessedDate) : null,
                        assessmentState: a.assessmentState
                    })));
                });
        };

        getAssessments();
    }, [submitted, decisionSaved]);

    useEffect(() => {
        if (!assessmentIdToEdit) return;

        const getQuestions = async () => {
            try {
                const response = await apiAdmin.get(`/get-assessment-data?assessmentId=${assessmentIdToEdit}`);

                if (response.data) {
                    setAssessmentToEdit(response.data.questions);
                    setAssessmentNotes(response.data.notes);
                    setSelectedAssessmentData(response.data.assessmentData);
                    setSelectedBuilderName(response.data.assessmentData?.builderName || "");
                }
            } catch (error) {
                setError("Error fetching assessment data:", error);
            }
        };

        getQuestions();
    }, [assessmentIdToEdit]);

    useEffect(() => {
        const statusFilters = {
            'Needs Approval': s => s.assessmentState === 'Needs Approval',
            'Approved': s => s.assessmentState?.includes('Approved'),
            'Denied': s => s.assessmentState?.includes('Denied'),
        };

        const filterFn = statusFilters[builderStatus] || (() => true);
        setBuildersToShow(assessments.filter(filterFn));
        setIsLoading(false);
    }, [assessments, builderStatus]);

    function EditToolbar(props) {
        return (
            <CustomDataGridToolbar>
                <span className="btn-link btn" onClick={() => { setAssessmentIdToEdit(-1); setAssessmentToEdit([]); }}>
                    <MdAdd className="mb-1" /> Add Assessment
                </span>
                <div className="col">
                    <ToggleButtonGroup
                        className="float-end"
                        value={builderStatus}
                        exclusive
                        onChange={(e, newStatus) => { setBuilderStatus(newStatus) }}
                        aria-label="Builder Status Toggle"
                    >
                        <ToggleButton value="Needs Approval" aria-label="Needs Approval">{toggleButtonLabels.needsApproval}</ToggleButton>
                        <ToggleButton value="Approved" aria-label="Approved">{toggleButtonLabels.approved}</ToggleButton>
                        <ToggleButton value="Denied" aria-label="Denied">{toggleButtonLabels.denied}</ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </CustomDataGridToolbar>
        );
    }

    useEffect(() => {
        if (assessmentToEdit && assessmentToEdit.length > 0) {
            const defaultValues = {};

            assessmentToEdit.forEach(q => {
                const a = q.answer ?? {};
                defaultValues[`answer-${q.questionId}`] = a.answerValue ?? false;
                defaultValues[`quantity-${q.questionId}`] = a.quantity ?? "";
                defaultValues[`amount-${q.questionId}`] = a.amount ?? "";
                defaultValues[`comment-${q.questionId}`] = a.comment ?? "";
            });

            reset(defaultValues);
        }
    }, [assessmentToEdit]);

    const onSubmit = async (data) => {
        if (assessmentIdToEdit === -1 && !builderSearchCompleted) {
            setFormError("Please select a builder and search before submitting.");
            return;
        }
        setIsSubmitting(true);
        const answers = assessmentToEdit.map(question => {
            const answerValue = data[`answer-${question.questionId}`] ?? false;
            const quantity = data[`quantity-${question.questionId}`] ?? null;
            const amount = data[`amount-${question.questionId}`] ?? null;
            const comment = data[`comment-${question.questionId}`] ?? null;

            return {
                questionId: question.questionId,
                answerValue,
                quantity,
                amount,
                comment
            };
        });

        const hasThresholdFailure = answers.some(answer => {
            const question = assessmentToEdit.find(q => q.questionId === answer.questionId);
            if (!question) return false;

            const isAnswerYes = answer.answerValue === true;
            if (isAnswerYes) {
                const quantityFails = question.quantityThreshold !== null && answer.quantity > question.quantityThreshold;
                const amountFails = question.amountThreshold !== null && answer.amount > question.amountThreshold;

                return quantityFails || amountFails;
            }

            return false;
        });

        const assessmentState = hasThresholdFailure ? "Needs Approval" : "Approved Automatically";

        const answerObject = {
            assessmentId: assessmentIdToEdit,
            builderName: selectedBuilderName,
            submittedBy: adminUserProfile.displayName,
            submittedByEmail: adminUserProfile.email,
            submissionDate: new Date().toISOString(),
            assessmentState,
            answers
        };


        try {
            const response = await apiAdmin.post(`/save-assessment-answers`, answerObject);

            if (response.data.success) {
                setAssessmentIdToEdit(response.data.assessmentId);
                switch (assessmentState) {
                    case "Approved Automatically":
                        setShowApprovedModal(true);
                        if (response.data.success) {
                            await downloadAssessmentPdf(response.data.assessmentId);
                        }
                        break;
                    default:
                        setShowNeedsApprovalModal(true);
                        break;
                }

                setIsSubmitting(false);
                setSubmitted(true);
                setBuilderSearchCompleted(false);
                setFormError(null);
                reset();
            } else {
                setError("There was an error saving the answers. Please try again.");
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
        }
    };

    useEffect(() => {
        const getLeadership = async () => {
            apiAdmin.get('/get-leadership-team')
                .then((response) => {
                    setLeadershipTeam(response.data);
                });
        }
        getLeadership();
    }, []);

    const sendAssessmentApprovalRequest = async () => {
        setSending(true);
        const selectedIds = watch("leadershipTeam");
        const emailObject = {
            teamLeaders:
                leadershipTeam.filter(member => selectedIds.includes(member.teamMemberId))
                    .map(member => ({
                        teamMemberId: member.teamMemberId,
                        firstName: member.firstName,
                        lastName: member.lastName,
                        email: member.email
                    })),
            assessmentId: assessmentIdToEdit,
            builderName: selectedBuilderName,
            submittedBy: adminUserProfile.displayName,
            submittedByEmail: adminUserProfile.email
        };

        try {
            await apiAdmin.post("/send-approval-email", emailObject);
            setEmailSent(true);
        } catch (error) {
            setError(`Error sending approval email: ${error.message}`);
        } finally {
            setSending(false);
            setAssessmentIdToEdit(null);
            setAssessmentToEdit(null);
            reset();
        }
    };

    useEffect(() => {
        if (paramAssessment) {
            const getParameters = async () => {
                apiAdmin.get(`/get-assessment-parameter?queryString=${paramAssessment}`)
                    .then((response) => {
                        if (response.data.assessmentId) {
                            if (response.data.directFromEmail) {
                                setAssessmentIdToApprove(response.data.assessmentId);
                                setShowDecisionModal(true);
                            } else {
                                setAssessmentIdToEdit(response.data.assessmentId);
                            }
                        }
                    });
            };
            getParameters();
        }
    }, [paramAssessment]);

    const downloadAssessmentPdf = async (assessmentId) => {
        try {
            const response = await apiAdmin.get(`/download-assessment-pdf?assessmentId=${assessmentId}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `builderassessment-${assessmentId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError("Error downloading assessment PDF:", error);
        }
    };

    const handleAssessmentDecisionSubmit = async () => {
        setSendingDecision(true);

        const note = noteRef.current?.value || '';
        const status = statusRef.current?.value || 'approve';

        const decisionObject = {
            assessmentId: assessmentIdToApprove !== null ? assessmentIdToApprove : assessmentIdToEdit,
            note,
            status,
            noteBy: adminUserProfile.displayName
        };

        try {
            const response = await apiAdmin.post("/save-assessment-decision", decisionObject);
            if (response.data.success && decisionObject.status === "approve") {
                await downloadAssessmentPdf(decisionObject.assessmentId);
            }
            setDecisionSaved(true);
        } catch (error) {
            setError("Error saving assessment decision");
        } finally {
            setSendingDecision(false);
            setShowDecisionModal(false);
            setAssessmentIdToEdit(null);
            setAssessmentIdToApprove(null);
            setAssessmentToEdit(null);
            setAssessmentNotes([]);
            setOrderSummary(null);
            reset();
        }
    };

    const handleBuilderSearch = async () => {
        if (!builderQuery.trim()) return;

        setLoadingBuilderSearch(true);
        setBuilderSearchCompleted(false);

        try {
            const response = await apiAdmin.get(`/builder-summary?name=${encodeURIComponent(builderQuery)}`);
            const builderSummary = response.data[0];

            setOrderSummary(builderSummary);
            setSelectedBuilderName(builderSummary.builderName);
            setBuilderSearchCompleted(true);

            const normalizedBuilder = builderSummary.builderName.trim().toLowerCase();
            const matchingAssessment = assessments.find(a =>
                a.builder.trim().toLowerCase() === normalizedBuilder
            );

            if (matchingAssessment) {
                setExistingAssessmentId(matchingAssessment.id);
            } else {
                setExistingAssessmentId(null);
            }
        } catch (error) {
            setError("Error fetching builder summary:", error);
        } finally {
            setLoadingBuilderSearch(false);
        }
    };

    useEffect(() => {
        if (orderSummary) {

            const formatted = [
                {
                    label: "Closed Orders",
                    count: orderSummary.closedOrderCount,
                    dateLabel: "Most Recent Closed",
                    date: orderSummary.mostRecentClosedOrderDate
                },
                {
                    label: "Cancelled Orders",
                    count: orderSummary.cancelledOrderCount,
                    dateLabel: "Most Recent Cancelled",
                    date: orderSummary.mostRecentCancelledOrderDate
                },
                {
                    label: "Open Orders",
                    count: orderSummary.openOrderCount,
                    dateLabel: "Most Recent Open",
                    date: orderSummary.mostRecentOpenOrderDate
                }
            ].map(({ label, count, dateLabel, date }) => ({
                label,
                count,
                dateText: count > 0 && date ? `${dateLabel}: ${new Date(date).toLocaleDateString()}` : null
            }));

            setOrderSummaryDisplay(formatted);

            const summaryText = formatted
                .map(({ label, count, dateText }) =>
                    dateText ? `${label}: ${count} - ${dateText}` : `${label}: ${count}`
                )
                .join(", ");

            setValue("comment-18", summaryText);

        }
    }, [orderSummary]);

    return (
        <div>
            <title>Builder Assessment</title>
            <h1>Builder Assessment</h1>

            {assessmentToEdit || assessmentIdToEdit === -1 ? <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {assessmentIdToEdit === -1 ? (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="flex-grow-1 me-2">
                                <TextField label="Enter Builder Name" {...register("builderName", { required: "Builder name is required." })} error={!!errors.builderName} helperText={errors.builderName?.message} value={builderQuery} onChange={(e) => { setBuilderQuery(e.target.value); setValue("builderName", e.target.value); }} onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleBuilderSearch();
                                    }
                                }} className="w-100" />
                            </div>
                            <button type="button" className="btn btn-primary" onClick={handleBuilderSearch} disabled={loadingBuilderSearch}>
                                {loadingBuilderSearch ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                        <span role="status"> Searching...</span>
                                    </>
                                ) : (
                                    <>
                                        <MdSearch title="Search Builders" /> Search Builders
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="mb-3">
                            <h2>Builder:  {selectedBuilderName}</h2>
                        </div>
                    )}
                    {existingAssessmentId && (
                        <div className="alert alert-danger" role="alert">
                            <p>An assessment has already been created for {selectedBuilderName}.  Click the button below to view that assessment. </p>
                            <button className="btn btn-primary me-4" onClick={() => { setAssessmentIdToEdit(existingAssessmentId); setExistingAssessmentId(null); }}>
                                View Assessment
                            </button>
                        </div>
                    )}
                    {orderSummary && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h3 className="card-title mb-4">
                                    Order Summary for {selectedBuilderName}
                                </h3>
                                {orderSummaryDisplay.map(({ label, count, dateText }) => (
                                    <div className="row mb-2" key={label}>
                                        <div className="col-md-6">
                                            <strong>{label}:</strong> {count}
                                        </div>
                                        <div className="col-md-6">
                                            {dateText && <strong>{dateText}</strong>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mb-3">
                        <div className="row">
                            <strong>Submitted By: {selectedAssessmentData === null ? adminUserProfile.displayName : `${selectedAssessmentData.submittedBy} on ${new Date(selectedAssessmentData.submissionDate).toLocaleDateString()}`}
                            </strong>
                            {assessmentNotes.length > 0 && (
                                <div className="mt-4">
                                    <h3>Assessment Notes</h3>
                                    {assessmentNotes.map(note => (
                                        <div key={note.assessmentNoteId} className="card mb-3">
                                            <div className="card-body">
                                                <h5 className="card-title mb-1">
                                                    {note.noteState && (
                                                        <span className={`badge ${note.noteState === 'Approved' ? 'bg-success' : 'bg-danger'} me-2`}>
                                                            {note.noteState}
                                                        </span>
                                                    )}
                                                    {note.noteBy} on {new Date(note.noteDate).toLocaleString()}
                                                </h5>
                                                <p className="card-text">{note.note}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <h3>Questions</h3>
                    {assessmentToEdit.map(question => (
                        <AssessmentQuestion key={question.questionId} question={question} register={register} watch={watch} errors={errors} disabled={!!assessmentIdToApprove} />
                    ))}
                    {formError && (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
                            <span>{formError}</span>
                            <button type="button" className="btn-close" onClick={() => setFormError(null)} aria-label="Close"></button>
                        </div>
                    )}
                    <div className="pt-2">
                        {assessmentIdToApprove ? (
                            <button type="button" className="btn btn-primary me-4" onClick={() => setShowDecisionModal(true)}>Approve or Deny</button>
                        ) : (
                            <button type="submit" className="btn btn-primary me-4" disabled={existingAssessmentId || isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Submitting...
                                    </>
                                ) : (
                                    "Submit"
                                )}
                            </button>
                        )}
                        <button type="button" className="btn btn-primary" onClick={() => { setAssessmentToEdit(null); setAssessmentIdToEdit(null); setAssessmentNotes([]); setSubmitted(false); setError(null); setExistingAssessmentId(null); setSelectedBuilderName(null); setOrderSummary(null); setBuilderQuery(''); setFormError(null); setAssessmentIdToApprove(null); reset(); }}>Close</button>
                    </div>
                </form>
            </>
                :
                <>
                    {decisionSaved && <div className="alert alert-success alert-dismissible" role="alert">The assessment decision has been saved.<button type="button" className="btn-close" onClick={() => setDecisionSaved(false)} aria-label="Close"></button></div>}
                    {submitted && <div className="alert alert-success alert-dismissible" role="alert">Assessment saved successfully.<button type="button" className="btn-close" onClick={() => setSubmitted(false)} aria-label="Close"></button></div>}
                    {error && (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
                            <span>{error}</span>
                            <button type="button" className="btn-close" onClick={() => setError(false)} aria-label="Close"></button>
                        </div>
                    )}
                    <DataGridPro
                        rows={buildersToShow}
                        columns={columns}
                        loading={isLoading}
                        slotProps={{
                            loadingOverlay: {
                                variant: 'skeleton',
                                noRowsVariant: 'skeleton',
                            },
                        }}
                        autoHeight
                        rowHeight={38}
                        disableRowSelectionOnClick
                        alignItems="center"
                        slots={{
                            toolbar: EditToolbar,
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pagination
                        showToolbar
                    />
                </>
            }
            {showNeedsApprovalModal && (
                <div className={`modal fade ${showNeedsApprovalModal ? 'show d-block' : ''}`} tabIndex="-1" id="requestApprovalModal">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Assessment for {selectedBuilderName} Requires Ops Approval</h5>
                                <button type="button" className="btn-close" onClick={() => setShowNeedsApprovalModal(false)} />
                            </div>
                            {emailSent && <div className="alert alert-success alert-dismissible mt-2" role="alert">Approval Email Sent!<button type="button" className="btn-close" onClick={() => setEmailSent(false)} aria-label="Close"></button></div>}
                            <div className="modal-body">
                                <p>This assessment has failed due to one or more conditions.</p>
                                <p>Please select a team member to request approval from:</p>
                                <div className="mb-3">
                                    <div className="row">
                                        <FormControl fullWidth error={!!errors.leadershipTeam}>
                                            <InputLabel id="multiple-leadership-label">Leadership Team Member(s) *</InputLabel>
                                            <Controller
                                                name="leadershipTeam"
                                                control={control}
                                                defaultValue={[]}
                                                rules={{ required: 'At least one team member is required' }}
                                                render={({ field }) => (
                                                    <Select
                                                        labelId="multiple-leadership-label"
                                                        id="multiple-leadership"
                                                        multiple
                                                        value={field.value}
                                                        onChange={(event) => {
                                                            const selectedIds = event.target.value.map(id => parseInt(id));
                                                            field.onChange(selectedIds);
                                                        }}
                                                        input={<OutlinedInput label="Leadership Team Member(s)" />}
                                                        MenuProps={MenuProps}
                                                    >
                                                        {leadershipTeam.map(member => (
                                                            <MenuItem key={`member_${member.teamMemberId}`} value={member.teamMemberId}>
                                                                {`${member.firstName} ${member.lastName}`}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                            {errors.leadershipTeam && (
                                                <small className="text-danger">{errors.leadershipTeam.message}</small>
                                            )}
                                        </FormControl>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => { setShowNeedsApprovalModal(false); setAssessmentToEdit(null); setAssessmentIdToEdit(null); setAssessmentNotes([]); setSubmitted(false); setError(null); setSelectedBuilderName(null); setOrderSummary(null); setExistingAssessmentId(null); setSelectedBuilderName(null); setBuilderQuery(''); reset(); }}>Cancel</button>
                                <button type="button" className="btn btn-primary w-auto me-3" onClick={sendAssessmentApprovalRequest} disabled={sending || emailSent}>
                                    {sending ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                            <span role="status"> Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <MdOutlineEmail title="Send Request" /> Email Request
                                        </>
                                    )}
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showApprovedModal && (
                <div className={`modal fade ${showApprovedModal ? 'show d-block' : ''}`} tabIndex="-1" id="downloadModal">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Assessment for {selectedBuilderName} Approved Automatically</h5>
                                <button type="button" className="btn-close" onClick={() => setShowApprovedModal(false)} />
                            </div>
                            {emailSent && <div className="alert alert-success alert-dismissible mt-2" role="alert">Approval Email Sent.<button type="button" className="btn-close" onClick={() => setEmailSent(false)} aria-label="Close"></button></div>}
                            <div className="modal-body">
                                <p>This assessment has been automatically approved and has downloaded.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => { setShowApprovedModal(false); setAssessmentToEdit(null); setAssessmentIdToEdit(null); setAssessmentNotes([]); setSubmitted(false); setError(null); setSelectedBuilderName(null); setOrderSummary(null); setBuilderQuery(''); reset(); }}>Close</button>
                                <button type="button" className="btn btn-primary w-auto me-3" onClick={() => downloadAssessmentPdf(assessmentIdToEdit)} >
                                    <MdOutlineFileDownload title="Download Assessment" /> Re-download Assessment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showDecisionModal && (
                <div className={`modal fade ${showDecisionModal ? 'show d-block' : ''}`} tabIndex="-1" id="decisionModal">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Assessment Decision</h5>
                                <button type="button" className="btn-close" onClick={() => { setShowDecisionModal(false); setAssessmentToEdit(null); setAssessmentIdToEdit(null); setAssessmentNotes([]); setSubmitted(false); setError(null); setSelectedBuilderName(null); setOrderSummary(null); setBuilderQuery(''); setSelectedAssessmentData(null); setAssessmentIdToApprove(null); reset(); }} />
                            </div>
                            {decisionSaved && (
                                <div className="alert alert-success alert-dismissible mt-2" role="alert">
                                    Decision Submitted.
                                    <button type="button" className="btn-close" onClick={() => { setDecisionSaved(false); setAssessmentToEdit(null); setAssessmentIdToEdit(null); setAssessmentNotes([]); setSubmitted(false); setError(null); setSelectedBuilderName(null); setOrderSummary(null); setBuilderQuery(''); setSelectedAssessmentData(null); setAssessmentIdToApprove(null); reset(); }} aria-label="Close"></button>
                                </div>
                            )}
                            <div className="modal-body">
                                <p>Please enter a note and select approval status:</p>
                                <TextField label="Note" inputRef={noteRef} className="w-100 mb-3" multiline rows={3} />
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="approval-status-label">Approval Status</InputLabel>
                                    <Select labelId="approval-status-label" id="approval-status" defaultValue="approve" inputRef={statusRef} label="Approval Status">
                                        <MenuItem value="approve">Approve</MenuItem>
                                        <MenuItem value="deny">Deny</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDecisionModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary w-auto me-3" onClick={handleAssessmentDecisionSubmit} disabled={sendingDecision || decisionSaved}>
                                    {sendingDecision ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                            <span role="status"> Submitting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <MdCheckCircle title="Submit Decision" /> Submit
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BuilderAssessment;
